import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';
import { createOptionsCache } from '../../../core/options/options-cache.js';
import { renderOptionContent } from '../../../core/options/render-option-content.js';
import { createPortalDropdown } from '../../../core/dom/portal-dropdown.js';
import { renderChips } from '../../../components/option-picker/render-chips.js';
import { resolveChipLabelsFromOptions } from '../../../components/option-picker/resolve-chip-labels.js';
import { isAbortError } from '../../../core/http/is-abort-error.js';

/**
 * Compares two string arrays by ordered equality.
 *
 * @param {string[]} a
 * @param {string[]} b
 */
function arraysOrderedEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** @type {import('../../../contracts/filters.js').FilterDriver} */
export const multiselectFilterDriver = {
  type: 'multiselect',
  mount(container, ctx) {
    const { def, onChange } = ctx;

    if (def.loadOptions) {
      // --- Async path: chip + search + portal-dropdown + lookup hydration ---
      return mountAsync(container, ctx);
    }

    // --- Static path (backward compatible — small enums, no search) ---
    const group = createElement('div', {
      class: 'multiselect-group',
      id: `filter-${def.key}`,
    });
    container.appendChild(group);

    const selectedArr = Array.isArray(ctx.value) ? ctx.value : [];

    /** @type {HTMLInputElement[]} */
    const checkboxes = [];

    for (const opt of def.options ?? []) {
      const cbId = `filter-${def.key}-${opt.value}`;
      const cbLabel = createElement('label', { class: 'checkbox-label', for: cbId });
      const cb = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'checkbox',
        id: cbId,
        name: def.key,
        value: opt.value,
      }));
      if (selectedArr.includes(opt.value)) {
        cb.checked = true;
      }
      cb.addEventListener('change', () => {
        onChange(getCheckedValues());
      });
      checkboxes.push(cb);
      cbLabel.appendChild(cb);
      const labelContent = createElement('span', {});
      renderOptionContent(opt, labelContent);
      cbLabel.appendChild(labelContent);
      group.appendChild(cbLabel);
    }

    function getCheckedValues() {
      return checkboxes.filter(cb => cb.checked).map(cb => cb.value);
    }

    return {
      setValue(value) {
        const arr = Array.isArray(value) ? value : [];
        for (const cb of checkboxes) {
          cb.checked = arr.includes(cb.value);
        }
      },
      getValue() {
        return getCheckedValues();
      },
      destroy() {
        group.remove();
      },
    };
  },
};

/**
 * Async path: combobox-style multiselect with selected chips, server search
 * via debounced provider, and exact-value lookup hydration for selected
 * values that fall outside the current search page.
 *
 * Selected chips are NEVER pruned on a search rebuild (regression on the old
 * pruning behavior — that was a workaround for missing exact lookup, and is
 * now silent data loss). `onChange` always emits a snapshot, never the live
 * `currentValues` array.
 *
 * @param {HTMLElement} container
 * @param {import('../../../contracts/filters.js').FilterDriverContext} ctx
 * @returns {import('../../../contracts/filters.js').FilterDriverHandle}
 */
function mountAsync(container, ctx) {
  const { def, onChange, logger } = ctx;
  const loadOptionsFn = /** @type {NonNullable<typeof def.loadOptions>} */ (def.loadOptions);
  const lookupOptions = def.lookupOptions;

  let destroyed = false;
  /** @type {string[]} */
  let currentValues = Array.isArray(ctx.value) ? [.../** @type {string[]} */ (ctx.value)] : [];
  /** @type {import('../../../contracts/forms.js').FieldOption[]} */
  let currentOptions = [];
  let isOpen = false;
  let highlightedIndex = -1;
  let version = 0;
  /** @type {AbortController | null} */
  let lookupController = null;

  // --- DOM ---
  // Note: filter-bar emits `<label for="filter-${key}">` and a sibling
  // element with `id="filter-${key}-label"` (see filter-bar.js:206). The
  // input below carries `id="filter-${key}"` so the for-association points
  // at a labelable element; the label-id is referenced via `aria-labelledby`
  // for redundancy and to satisfy combobox accessibility-name guidance.
  const wrapper = createElement('div', { class: 'multiselect-filter' });
  const control = createElement('div', { class: 'multiselect-filter__control' });
  wrapper.appendChild(control);

  const chipsContainer = createElement('span', { class: 'multiselect-filter__chips' });
  control.appendChild(chipsContainer);

  const panelId = `multiselect-filter-panel-${def.key}`;
  const input = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text',
    class: 'multiselect-filter__input',
    id: `filter-${def.key}`,
    role: 'combobox',
    'aria-expanded': 'false',
    'aria-haspopup': 'listbox',
    'aria-controls': panelId,
    'aria-labelledby': `filter-${def.key}-label`,
    autocomplete: 'off',
    placeholder: def.placeholder ?? 'Search...',
  }));
  control.appendChild(input);

  // Clear button — visible only when the search query is non-empty. Reset path
  // for the search-string side of the UI (different from chip removal, which
  // resets the selection side). Without this, once the dropdown shows results
  // for "abc" the user has no obvious way to revert to the unfiltered list
  // short of selecting/deselecting an option (which some flows don't want).
  const clearBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    class: 'multiselect-filter__clear',
    'aria-label': 'Clear search',
    tabindex: '-1', // keyboard-driven users use Esc / clearSearch via Backspace
    hidden: 'hidden',
  }));
  clearBtn.textContent = '×';
  control.appendChild(clearBtn);

  function updateClearButtonVisibility() {
    if (input.value === '') clearBtn.setAttribute('hidden', 'hidden');
    else clearBtn.removeAttribute('hidden');
  }

  /**
   * Resets the search query to '' and refocuses the input. Used by:
   *  - explicit clear button click
   *  - toggleOption (after a selection commits, the user is starting a fresh search)
   *  - chip removal (selection set changed, search context becomes stale)
   */
  function clearSearch() {
    if (input.value === '') return;
    input.value = '';
    updateClearButtonVisibility();
    provider.load('');
    input.focus();
  }

  const panel = createElement('div', { class: 'multiselect-filter__dropdown' });
  panel.style.display = 'none';
  const optionsList = createElement('div', {
    class: 'multiselect-filter__options',
    role: 'listbox',
    'aria-multiselectable': 'true',
    id: panelId,
  });
  panel.appendChild(optionsList);

  container.appendChild(wrapper);

  // --- Options provider with TTL cache ---
  const cache = createOptionsCache();
  const cacheNs = `multiselect-filter:${def.key}`;
  /** @type {import('../../../contracts/forms.js').OptionLoader} */
  const cachedLoadOptions = async (opts) => {
    const cacheKey = JSON.stringify({ q: opts.query });
    if (cache.has(cacheNs, cacheKey)) {
      return /** @type {any} */ (cache.get(cacheNs, cacheKey));
    }
    const result = await loadOptionsFn(opts);
    cache.set(cacheNs, cacheKey, result);
    return result;
  };
  const provider = createOptionsProvider({
    loadOptions: cachedLoadOptions,
    mapOption: def.mapOption,
    debounceMs: 300,
  });

  const portal = createPortalDropdown({
    anchor: wrapper,
    panel,
    onClose: () => closeDropdown(),
  });

  /** @type {Map<string, string>} */
  const chipLabels = new Map();

  function doRenderChips() {
    renderChips(chipsContainer, {
      values: currentValues,
      labels: chipLabels,
      classes: { chip: 'multiselect-filter__chip', removeBtn: 'multiselect-filter__chip-remove' },
      onRemove: (id) => removeValue(id),
      disabled: false,
    });
  }

  /** @param {string} id */
  function removeValue(id) {
    const idx = currentValues.indexOf(id);
    if (idx === -1) return;
    currentValues = [...currentValues.slice(0, idx), ...currentValues.slice(idx + 1)];
    chipLabels.delete(id);
    doRenderChips();
    // Clear any active search — selection set changed, leaving a stale query
    // displayed on the dropdown is confusing UX. Clears via input.value=''
    // and triggers a fresh provider.load(''); refocuses the input.
    clearSearch();
    if (isOpen) renderOptions();
    // Restart hydration: aborts any in-flight lookup for the prior value-set,
    // then issues a fresh lookup for the (possibly still-unresolved) remaining
    // values. Without this, removing a chip during a pending initial lookup
    // would abort it permanently and leave the other chips stuck as `#id`.
    hydrateLabels();
    onChange([...currentValues]);
  }

  /**
   * Aborts any in-flight lookup and bumps the version counter so a late resolve
   * cannot mutate state for a stale value-set. Called on EVERY semantic value
   * change (initial mount, setValue, chip-X, dropdown toggle) so a stale
   * lookup can never repopulate chipLabels for values that no longer exist.
   */
  function invalidateHydration() {
    version++;
    if (lookupController) {
      lookupController.abort();
      lookupController = null;
    }
  }

  /**
   * Aborts any in-flight lookup, then issues a fresh one for the current
   * values. No-op when there are no values or no lookupOptions provided.
   * Both early-exit paths still invalidate first so a setValue([]) (Reset/
   * URL rehydrate to empty) does not leave a prior lookup alive.
   */
  function hydrateLabels() {
    invalidateHydration();
    if (!lookupOptions || currentValues.length === 0) return;
    lookupController = new AbortController();
    const signal = lookupController.signal;
    const runVersion = version;
    lookupOptions([...currentValues], { signal })
      .then((opts) => {
        if (destroyed || runVersion !== version) return;
        // Update only `#id` placeholders; never overwrite resolved labels.
        let changed = false;
        for (const opt of opts) {
          const current = chipLabels.get(opt.value);
          if (current == null || current.startsWith('#')) {
            chipLabels.set(opt.value, opt.label);
            changed = true;
          }
        }
        if (changed) doRenderChips();
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        // Late check: the rejection might have arrived after a superseding
        // call invalidated this run. Don't emit a noisy warning for a stale
        // failure the user is no longer interested in.
        if (destroyed || runVersion !== version) return;
        // Lookup helper already logged the technical err. Driver adds
        // filter-key context at WARN level for diagnosability.
        logger?.warn?.('Filter selected labels could not be hydrated', {
          data: { filterKey: def.key },
        });
      });
  }

  // Initial chip render with `#id` placeholders.
  for (const id of currentValues) chipLabels.set(id, `#${id}`);
  doRenderChips();
  hydrateLabels();

  const unsubscribe = subscribeWithInitial(provider, (state) => {
    if (destroyed) return;
    wrapper.setAttribute('aria-busy', state.loading ? 'true' : 'false');
    currentOptions = state.options.slice();
    const { changed } = resolveChipLabelsFromOptions({
      values: currentValues,
      labels: chipLabels,
      options: currentOptions,
    });
    if (changed) doRenderChips();
    if (isOpen) renderOptions();
  });

  provider.load('');

  function renderOptions() {
    optionsList.textContent = '';
    highlightedIndex = -1;
    currentOptions.forEach((opt, idx) => {
      const isSelected = currentValues.includes(opt.value);
      const optEl = createElement('div', {
        role: 'option',
        class: 'multiselect-filter__option' + (isSelected ? ' multiselect-filter__option--selected' : ''),
        'aria-selected': isSelected ? 'true' : 'false',
        id: `multiselect-filter-${def.key}-opt-${idx}`,
      });
      renderOptionContent(opt, optEl);
      optEl.addEventListener('mousedown', (e) => {
        e.preventDefault();
        // toggleOption rebuilds optionsList, detaching the mousedown target.
        portal.suppressNextOutsideClose();
        toggleOption(opt);
      });
      optEl.addEventListener('mouseover', () => setHighlight(idx));
      optionsList.appendChild(optEl);
    });
  }

  /** @param {number} idx */
  function setHighlight(idx) {
    const items = optionsList.querySelectorAll('.multiselect-filter__option');
    items.forEach((item, i) => {
      item.classList.toggle('multiselect-filter__option--highlighted', i === idx);
    });
    highlightedIndex = idx;
    if (idx >= 0 && items[idx]) {
      input.setAttribute('aria-activedescendant', items[idx].id || '');
    }
  }

  /** @param {import('../../../contracts/forms.js').FieldOption} opt */
  function toggleOption(opt) {
    if (currentValues.includes(opt.value)) {
      // Toggle off — remove. Allocate new array, snapshot for onChange.
      const idx = currentValues.indexOf(opt.value);
      currentValues = [...currentValues.slice(0, idx), ...currentValues.slice(idx + 1)];
      chipLabels.delete(opt.value);
    } else {
      currentValues = [...currentValues, opt.value];
      // Source label directly from the option being toggled — no lookup needed
      // for THIS value, but `hydrateLabels()` below still re-fetches any other
      // selected values that are still unresolved.
      chipLabels.set(opt.value, opt.label);
    }
    doRenderChips();
    // Clear the search after every selection change. The dropdown stays open
    // (multi-select UX), and clearing the query gives the user the unfiltered
    // option list ready for the next selection. clearSearch() is silent if
    // input is already empty.
    clearSearch();
    renderOptions();
    // Restart hydration: aborts any in-flight lookup (so a stale resolve from
    // the prior value-set can't write back) AND issues a fresh lookup for any
    // still-unresolved chips. resolveChipLabelsFromOptions semantics ensure
    // the just-toggled value's label is not overwritten.
    hydrateLabels();
    onChange([...currentValues]);
    input.focus();
  }

  function openDropdown() {
    if (isOpen) return;
    isOpen = true;
    input.setAttribute('aria-expanded', 'true');
    portal.open();
    provider.load(input.value);
    renderOptions();
  }

  function closeDropdown() {
    if (!isOpen) return;
    isOpen = false;
    input.setAttribute('aria-expanded', 'false');
    portal.close();
    input.removeAttribute('aria-activedescendant');
  }

  // --- Events ---
  input.addEventListener('focus', () => openDropdown());
  input.addEventListener('input', () => {
    if (!isOpen) openDropdown();
    provider.load(input.value);
    updateClearButtonVisibility();
  });

  // Clear button uses `mousedown + preventDefault` so the input doesn't blur
  // before the click handler runs (preventDefault on mousedown keeps focus on
  // the input). A bare `click` listener would let the browser blur the input
  // first, which on Chrome can dismiss the focused-state styling and produce
  // a visible flicker.
  clearBtn.addEventListener('mousedown', (e) => { e.preventDefault(); });
  clearBtn.addEventListener('click', () => clearSearch());

  // Refocus on control click — but only when the click target is not the
  // input itself (a click ON the input already gives it focus, and re-calling
  // input.focus() during a typing burst can interrupt IME composition or
  // cause a visible focus flicker on Chrome).
  control.addEventListener('click', (e) => {
    if (e.target === input || e.target === clearBtn) return;
    input.focus();
  });

  /** @param {KeyboardEvent} e */
  function handleKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setHighlight(Math.min(highlightedIndex + 1, currentOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setHighlight(Math.max(highlightedIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else if (highlightedIndex >= 0 && currentOptions[highlightedIndex]) {
        toggleOption(currentOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Progressive escape: first Esc clears a non-empty search; second Esc
      // (or first if search is already empty) closes the dropdown. Matches
      // common combobox patterns and gives keyboard users a way to reset
      // the query without reaching for the mouse.
      if (input.value !== '') {
        clearSearch();
      } else {
        closeDropdown();
      }
    }
  }
  input.addEventListener('keydown', handleKeydown);

  return {
    setValue(value) {
      const next = Array.isArray(value) ? [.../** @type {string[]} */ (value)] : [];
      const hadQuery = input.value !== '';
      if (arraysOrderedEqual(currentValues, next) && !hadQuery) {
        // No-op: same content & order, no transient query to clear.
        return;
      }
      currentValues = next;
      // Prune chip labels to the new value set (avoids unbounded growth).
      const valueSet = new Set(currentValues);
      for (const key of [...chipLabels.keys()]) {
        if (!valueSet.has(key)) chipLabels.delete(key);
      }
      // Seed `#id` placeholders for new values.
      for (const id of currentValues) {
        if (!chipLabels.has(id)) chipLabels.set(id, `#${id}`);
      }
      doRenderChips();
      // Clear any transient search query so URL-rehydrate / Reset produces a
      // neutral UI (no stale query in input, no filtered dropdown). Skip the
      // focus side effect of clearSearch() — host-driven path should not
      // disturb where the user's focus currently is.
      if (hadQuery) {
        input.value = '';
        updateClearButtonVisibility();
        provider.load('');
      }
      if (isOpen) renderOptions();
      hydrateLabels();
      // Silent — host-driven (URL rehydrate or Reset).
    },
    getValue() {
      return [...currentValues];
    },
    destroy() {
      destroyed = true;
      version++;
      if (lookupController) lookupController.abort();
      unsubscribe();
      provider.destroy();
      cache.destroy();
      closeDropdown();
      portal.destroy();
      wrapper.remove();
    },
  };
}
